import serviceUrls from '@oncolens/oncolens-ui/lib/api/service-urls';
import api from '@/utils/httpClient';
import { TypeaheadListItem } from '@oncolens/oncolens-ui/lib/components/FormControls/Typeahead/Typeahead';
import { AxiosError } from 'axios';
import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from 'react-query';
import {
  BiomarkerDto,
  BiomarkerForCreationDto,
  BiomarkerForUpdateDto,
  BiomarkerPanelDto,
  BiomarkerPanelForCreationDto,
  BiomarkerPanelForUpdateDto,
} from '../dtos/Biomarker';
import { biomarkerKeys } from '../Biomarkers/biomarkers.keys';
import { ApiListResponse, ApiResponse } from '@oncolens/oncolens-ui/lib/dto/api/ApiResponse';
import { noop } from 'lodash';
import { nanoid } from 'nanoid';

async function getBiomarkers(patientId: string) {
  const config: any = { containsPHI: false };
  return api
    .get(`/api/panels?filters=patientId==${patientId}&sortorder=sortorder`, config)
    .then((response) => {
      const parsedResponse = response.data as ApiListResponse<BiomarkerPanelDto>;

      return parsedResponse.data;
    });
}

function useGetBiomarkers(patientId: string, options?: UseQueryOptions<BiomarkerPanelDto[]>) {
  return useQuery(biomarkerKeys.panels(patientId), async () => getBiomarkers(patientId), options);
}

export interface BiomarkerApiItem {
  gene: string;
  name: string;
  uuid: string;
  variant_aliases: string[];
  gene_aliases: string[];
}

function queryBiomarkersApi(query: string): Promise<TypeaheadListItem[]> {
  return api
    .get(`${serviceUrls.BIOMARKERS_API.BASE()}/api/biomarkers/?query=${query}`)
    .then((res: any) => {
      const items = (res.data as BiomarkerApiItem[]).map((item) => {
        return {
          label: item.name,
          value: item.uuid,
          alias: item.gene,
        } as TypeaheadListItem;
      });
      return items;
    })
    .catch(() => {
      return [];
    });
}

export interface CreateBiomarkerPanelsParameters {
  patientId: string;
  data: BiomarkerPanelForCreationDto[];
}

async function createPanels(patientId: string, data: BiomarkerPanelForCreationDto[]) {
  const config: any = { containsPHI: false };
  return api.post(`/api/panels?patientId=${patientId}`, data, config).then((response) => {
    const parsedResponse = response.data as ApiListResponse<BiomarkerPanelDto>;

    return parsedResponse.data;
  });
}

function useCreateBiomarkerPanels(
  options?: UseMutationOptions<BiomarkerPanelDto[], AxiosError, CreateBiomarkerPanelsParameters>
) {
  return useMutation(
    async ({ patientId, data }: CreateBiomarkerPanelsParameters) => createPanels(patientId, data),
    options
  );
}

export interface UpdateBiomarkerPanelParameters {
  panelId: string;
  data: BiomarkerPanelForUpdateDto;
}

async function updatePanel(panelId: string, data: BiomarkerPanelForUpdateDto) {
  const config: any = { containsPHI: false };
  return api
    .put(`/api/panels/${panelId}`, data, config)
    .then((response) => (response.data as ApiResponse<BiomarkerPanelDto>).data);
}

function useUpdateBiomarkerPanel(
  options?: UseMutationOptions<BiomarkerPanelDto, AxiosError, UpdateBiomarkerPanelParameters>
) {
  return useMutation(
    async ({ panelId, data }: UpdateBiomarkerPanelParameters) => updatePanel(panelId, data),
    options
  );
}

async function deletePanel(panelId: string) {
  const config: any = { containsPHI: false };
  return api
    .delete(`/api/panels/${panelId}`, config)
    .then((response) => (response.data as ApiResponse<any>).data);
}

function useDeleteBiomarkerPanel(options?: UseMutationOptions<any, AxiosError, any>) {
  return useMutation(async (panelId: string) => deletePanel(panelId), options);
}

interface CreateBiomarkerParameters {
  panelId: string;
  data: BiomarkerForCreationDto;
}

async function createBiomarker(panelId: string, data: BiomarkerForCreationDto) {
  const config: any = { containsPHI: false };
  return api
    .post(`/api/biomarkers?panelId=${panelId}`, data, config)
    .then((response) => (response.data as ApiResponse<BiomarkerDto>).data);
}

function useCreateBiomarker(
  options?: UseMutationOptions<BiomarkerDto, AxiosError, CreateBiomarkerParameters>
) {
  return useMutation(
    async ({ panelId, data }: CreateBiomarkerParameters) => createBiomarker(panelId, data),
    options
  );
}

interface UpdateBiomarkerParameters {
  biomarkerId: string;
  data: BiomarkerForUpdateDto;
}

async function updateBiomarker(biomarkerId: string, data: BiomarkerForUpdateDto) {
  const config: any = { containsPHI: false };
  return api
    .put(`/api/biomarkers/${biomarkerId}`, data, config)
    .then((response) => (response.data as ApiResponse<BiomarkerDto>).data);
}

function useUpdateBiomarker(
  options?: UseMutationOptions<BiomarkerDto, AxiosError, UpdateBiomarkerParameters>
) {
  return useMutation(
    async ({ biomarkerId, data }: UpdateBiomarkerParameters) => updateBiomarker(biomarkerId, data),
    options
  );
}

async function deleteBiomarker(biomarkerId: string) {
  const config: any = { containsPHI: false };
  return api
    .delete(`/api/biomarkers/${biomarkerId}`, config)
    .then((response) => (response.data as ApiResponse<any>).data);
}

function useDeleteBiomarker(options?: UseMutationOptions<any, AxiosError, string>) {
  return useMutation(async (biomarkerId: string) => deleteBiomarker(biomarkerId), options);
}

async function updatePanelsSortOrder(
  patientId: string,
  itemsToUpdate: UpdatePanelSortOrderParameters[]
) {
  const config: any = { containsPHI: false };
  return api.put(`/api/panels/sortorder?patientId=${patientId}`, itemsToUpdate, config).then(noop);
}

interface UpdatePanelSortOrderParameters {
  id: string;
  sortOrder: number;
}

function useUpdatePanelsSortOrder(
  options?: UseMutationOptions<
    void,
    AxiosError,
    { patientId: string; itemsToUpdate: UpdatePanelSortOrderParameters[] }
  >
) {
  return useMutation(
    async ({ patientId, itemsToUpdate }) => updatePanelsSortOrder(patientId, itemsToUpdate),
    options
  );
}

// mocked mutations with no network call
async function createBiomarkerMocked(panelId: string, data: BiomarkerForCreationDto) {
  return data as BiomarkerDto;
}

function useCreateBiomarkerMocked(
  options?: UseMutationOptions<BiomarkerDto, AxiosError, CreateBiomarkerParameters>
) {
  return useMutation(
    async ({ panelId, data }: CreateBiomarkerParameters) => createBiomarkerMocked(panelId, data),
    options
  );
}

async function updateBiomarkerMocked(biomarkerId: string, data: BiomarkerForUpdateDto) {
  return data as BiomarkerDto;
}

function useUpdateBiomarkerMocked(
  options?: UseMutationOptions<BiomarkerDto, AxiosError, UpdateBiomarkerParameters>
) {
  return useMutation(
    async ({ biomarkerId, data }: UpdateBiomarkerParameters) =>
      updateBiomarkerMocked(biomarkerId, data),
    options
  );
}

async function deleteBiomarkerMocked(biomarkerId: string) {
  return biomarkerId;
}

function useDeleteBiomarkerMocked(options?: UseMutationOptions<any, AxiosError, string>) {
  return useMutation(async (biomarkerId: string) => deleteBiomarkerMocked(biomarkerId), options);
}

// mocked network calls for edit request
async function createPanelsMocked(patientId: string, data: BiomarkerPanelForCreationDto[]) {
  return data as BiomarkerPanelDto[];
}

function useCreateBiomarkerPanelsMocked(
  options?: UseMutationOptions<BiomarkerPanelDto[], AxiosError, CreateBiomarkerPanelsParameters>
) {
  return useMutation(
    async ({ patientId, data }: CreateBiomarkerPanelsParameters) =>
      createPanelsMocked(patientId, data),
    options
  );
}

async function updatePanelMocked(panelId: string, data: BiomarkerPanelForUpdateDto) {
  return data as BiomarkerPanelDto;
}

function useUpdateBiomarkerPanelMocked(
  options?: UseMutationOptions<BiomarkerPanelDto, AxiosError, UpdateBiomarkerPanelParameters>
) {
  return useMutation(
    async ({ panelId, data }: UpdateBiomarkerPanelParameters) => updatePanelMocked(panelId, data),
    options
  );
}

async function deletePanelMocked(panelId: string) {
  return panelId;
}

function useDeleteBiomarkerPanelMocked(options?: UseMutationOptions<any, AxiosError, any>) {
  return useMutation(async (panelId: string) => deletePanelMocked(panelId), options);
}

export {
  useGetBiomarkers,
  useCreateBiomarkerPanels,
  createPanels,
  useUpdateBiomarkerPanel,
  useDeleteBiomarkerPanel,
  useCreateBiomarker,
  useUpdateBiomarker,
  useDeleteBiomarker,
  getBiomarkers,
  queryBiomarkersApi,
  useUpdatePanelsSortOrder,
  useCreateBiomarkerMocked,
  useUpdateBiomarkerMocked,
  useDeleteBiomarkerMocked,
  useCreateBiomarkerPanelsMocked,
  useUpdateBiomarkerPanelMocked,
  useDeleteBiomarkerPanelMocked,
};
